<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const faq = [
  {
    q: 'Berapa banyak kategori yang bisa saya buat?',
    a: 'Tidak ada batas jumlah kategori. Namun untuk kemudahan pelaporan, disarankan maksimal 10-15 kategori. Terlalu banyak kategori justru membuat laporan pengeluaran sulit dibaca.',
  },
  {
    q: 'Apakah kategori bisa dihapus setelah dipakai?',
    a: 'Tidak bisa. Jika sebuah kategori sudah digunakan oleh catatan pengeluaran, kategori tersebut tidak bisa dihapus. Solusinya: nonaktifkan kategori tersebut dengan mematikan toggle "Kategori Aktif" saat edit.',
  },
  {
    q: 'Apa fungsi warna pada kategori?',
    a: 'Warna membantu identifikasi visual di laporan dan daftar pengeluaran. Misalnya, kategori "Bahan Baku" berwarna hijau dan "Listrik & Air" berwarna kuning — Anda bisa langsung bedakan keduanya tanpa membaca teksnya.',
  },
  {
    q: 'Apakah warna kategori muncul di struk atau laporan cetak?',
    a: 'Warna muncul di tampilan layar (aplikasi web). Untuk laporan cetak atau ekspor PDF, warna mungkin ditampilkan sebagai titik berwarna kecil di sebelah nama kategori, tergantung format laporan.',
  },
  {
    q: 'Bolehkah dua kategori punya warna yang sama?',
    a: 'Boleh secara teknis, tapi tidak disarankan. Tujuan warna adalah membedakan kategori secara visual. Jika dua kategori berwarna sama, Anda harus membaca teksnya untuk membedakan keduanya.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/keuangan" class="text-decoration-none text-medium-emphasis text-body-2">
            Keuangan
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Kategori Pengeluaran</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-4 mb-4">
          <VAvatar color="error" variant="tonal" size="64" rounded="lg">
            <VIcon icon="ri-price-tag-3-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Kategori Pengeluaran</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Atur pengelompokan jenis pengeluaran toko Anda
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Panduan membuat dan mengelola kategori pengeluaran. Kategori harus dibuat
          terlebih dahulu sebelum Anda bisa mencatat pengeluaran operasional.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="error" />
            Apa itu Kategori Pengeluaran?
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <p class="text-body-2 mb-3">
              <strong>Kategori pengeluaran</strong> adalah label pengelompokan untuk setiap
              biaya yang Anda keluarkan. Fungsinya sama seperti amplop-amplop di dompet
              —  setiap amplop punya label berbeda: "Belanja Dapur", "Tagihan Listrik",
              "Gaji Karyawan", dan sebagainya.
            </p>
            <p class="text-body-2 mb-4">
              Dengan kategori yang rapi, laporan keuangan Anda jadi lebih mudah dibaca.
              Anda bisa langsung tahu: bulan ini paling banyak keluar untuk apa?
            </p>

            <VAlert type="success" variant="tonal">
              <p class="font-weight-bold mb-2">
                🏪 Contoh kategori untuk coffee shop:
              </p>
              <VRow>
                <VCol cols="6" sm="4" md="3" v-for="cat in [
                  { nama: 'Bahan Baku', warna: '#22C55E', contoh: 'Kopi, susu, sirup' },
                  { nama: 'Peralatan', warna: '#3B82F6', contoh: 'Mesin kopi, blender' },
                  { nama: 'Listrik & Air', warna: '#EAB308', contoh: 'Tagihan bulanan' },
                  { nama: 'Gaji', warna: '#8B5CF6', contoh: 'Upah karyawan' },
                  { nama: 'Kemasan', warna: '#F97316', contoh: 'Cup, sedotan, kantong' },
                  { nama: 'Lain-lain', warna: '#6B7280', contoh: 'Pengeluaran tak terduga' },
                ]" :key="cat.nama">
                  <div class="d-flex align-center gap-2 mb-2">
                    <div :style="`background:${cat.warna};width:12px;height:12px;border-radius:3px;flex-shrink:0`" />
                    <div>
                      <p class="text-body-2 font-weight-bold mb-0">{{ cat.nama }}</p>
                      <p class="text-caption text-medium-emphasis mb-0">{{ cat.contoh }}</p>
                    </div>
                  </div>
                </VCol>
              </VRow>
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara tambah -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Menambah Kategori Baru
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Pengeluaran → Kategori</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Tambah Kategori"</strong> di pojok kanan atas
              </li>
              <li class="mb-4">
                Isi form yang muncul:
                <VTable class="mt-3">
                  <thead>
                    <tr>
                      <th style="min-width:140px">Field</th>
                      <th style="min-width:80px">Wajib?</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Nama Kategori</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Nama yang jelas dan deskriptif.<br>
                        Contoh: <strong>Bahan Baku</strong>, <strong>Tagihan Listrik</strong>,
                        <strong>Gaji Karyawan</strong>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Warna</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Klik salah satu lingkaran warna yang tersedia, atau ketik kode warna
                        (format: <code>#RRGGBB</code>).<br>
                        Sistem sudah menyediakan 10 pilihan warna — Anda tidak perlu ketik
                        kode warna secara manual jika tidak mau.<br>
                        <span class="text-caption text-medium-emphasis">
                          Contoh kode: #22C55E (hijau), #EF4444 (merah), #3B82F6 (biru)
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Kategori Aktif</strong></td>
                      <td><VChip color="secondary" size="x-small" variant="tonal">❌ Opsional</VChip></td>
                      <td class="text-body-2 py-2">
                        Toggle on/off. Default: aktif.<br>
                        Kategori yang nonaktif tidak bisa dipilih saat mencatat pengeluaran baru,
                        tapi data lama tetap tersimpan.
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan"</strong> — kategori langsung tersedia saat mencatat pengeluaran ✅
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Edit & nonaktifkan -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-edit-line" class="me-2" color="warning" />
            Edit Kategori
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Klik ikon <strong>pensil (✏️)</strong> di baris kategori</li>
              <li class="mb-2">Ubah nama, warna, atau status aktif</li>
              <li>Klik <strong>"Simpan"</strong></li>
            </ol>
            <VAlert type="info" variant="tonal" density="compact">
              Mengubah nama kategori akan langsung mengubah tampilan di semua catatan
              pengeluaran yang menggunakan kategori tersebut.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-delete-bin-line" class="me-2" color="error" />
            Hapus Kategori
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Klik ikon <strong>tempat sampah (🗑️)</strong></li>
              <li>Konfirmasi di dialog yang muncul</li>
            </ol>
            <VAlert type="error" variant="tonal" density="compact">
              <strong>Tidak bisa dihapus</strong> jika kategori sudah pernah dipakai
              untuk mencatat pengeluaran. Nonaktifkan saja jika tidak mau dipakai lagi.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-5 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText class="pa-5 pt-3">
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Buat kategori sebelum mencatat pengeluaran pertama.</strong>
                  Tanpa kategori, Anda tidak bisa menyimpan catatan pengeluaran.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan warna berbeda untuk setiap kategori.</strong>
                  Di laporan, Anda bisa langsung membedakan kategori secara visual
                  tanpa membaca teks.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Jangan buat kategori terlalu spesifik.</strong>
                  "Bahan Baku" lebih baik daripada "Kopi Arabika" dan "Susu UHT" sebagai
                  kategori terpisah — gunakan deskripsi di catatan pengeluaran untuk detail.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6 mb-2">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Langkah Selanjutnya</h5>
        <VRow>
          <VCol v-for="link in [
            { title: 'Catat Pengeluaran', sub: 'Setelah kategori siap, mulai catat pengeluaran', icon: 'ri-wallet-3-line', color: 'error', to: '/userguide/keuangan/pengeluaran' },
            { title: 'Laporan Laba Rugi', sub: 'Lihat dampak pengeluaran ke keuntungan toko', icon: 'ri-bar-chart-line', color: 'primary', to: '/userguide/laporan/laba-rugi' },
          ]" :key="link.title" cols="12" md="6">
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
